import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { always, identity } from 'utils/helper/functions';
import { useStorageSync } from 'utils/storage/useStorageSync';

export function usePersistParams(key, { filter = identity, enable = always } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const disabled = !enable(searchParams);
  const location = useLocation();
  const [storage, setStorage, clearStorage] = useStorageSync(useMemo(() => [location.pathname, key], [location.pathname, key]));
  const mountingRef = useRef(true);
  const [initial] = useState(() => {
    if (!searchParams.size && storage && !disabled) {
      return new URLSearchParams(storage);
    }
  });

  const [restoring, setRestoring] = useState(Boolean(initial));

  useEffect(() => {
    if (mountingRef.current || disabled) {
      return;
    }

    const newParams = new URLSearchParams(filter(Object.fromEntries(searchParams)));

    if (!newParams.size) {
      clearStorage();
    } else {
      setStorage(newParams.toString());
    }
  }, [searchParams, setStorage, filter, clearStorage, disabled]);

  useEffect(() => {
    mountingRef.current = false;

    if (initial) {
      setSearchParams(initial, { replace: true });
      setRestoring(false);
    }

    return () => {
      mountingRef.current = true; // due to strict mode
    };
  }, []); // eslint-disable-line

  return { initial, restoring };
}
